<template>
  <div v-show="error" class="error">
    <h1>404</h1>
    <p>We're sorry but something went wrong...</p>
  </div>
  <div style="width: 100%; height: 100%; overflow: hidden; background-color: transparent;" v-show="loading && !error"
    key="1">
    <div class="spinner">
      <div class="double-bounce1"></div>
      <div class="double-bounce2"></div>
    </div>
  </div>

  <div style="width: 100%; height: 100%;" v-show="!loading && !error" key="4">
    <div style="width: 100%; height: 100%; border: 0; position: absolute; top: 0px;" key="3" ref="templateDiv1"
      id="frame1"></div>
    <div style="width: 100%; height: 100%; border: 0; position: absolute; top: 0px;" key="5" ref="templateDiv2"
      id="frame2"></div>
  </div>
</template>

<script>
import axios from 'axios';
import { createApp, h } from 'vue';
import { compile } from '@vue/compiler-dom';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import he from 'he';
import localForage from "localforage";
import videojs from 'video.js';

const blockElements = [
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DETAILS', 'DIALOG', 'DD', 'DIV', 'DL',
  'DT', 'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION',
  'TABLE', 'UL'
];

const blockElementExclusions = ['P', 'SPAN', 'B', 'I', 'EM', 'STRONG', 'A', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
const maxCompatCheckLevels = 4;

export default {
  name: 'TemplatePreview',
  inheritAttrs: false,
  data() {
    return {
      loading: true,
      changeSubject: new Subject(true),
      loadingSubject: new Subject(true),
      frameToggle: false,
      index: 1,
      payloadObject: {},
      culturesObject: {},
      cache: new Map(),
      templateUrl: '',
      error: false,
      expirationTimeout: 1000 * 30,
      uriUpdated: false,
      payloadUpdated: false,
      videoJsOptions: {
        muted: false,
        autoplay: false,
        controls: true,
        preload: 'auto'
      }
    };
  },
  emits: ['loading'],
  props: {
    payload: String,
    uri: String,
    preview: {
      default: true,
      type: Boolean
    },
    cultures: String,
    culture: {
      default: 'en-US',
      type: String
    }
  },
  watch: {
    uri: {
      handler: 'onUriChange',
      deep: true
    },
    payload: {
      handler: 'onPayloadChange',
      deep: true
    },
    cultures: {
      handler: 'onCulturesChange',
      deep: true
    },
    culture: {
      handler: 'onCultureChange',
      deep: true
    }
  },
  created() {
    this.changeSubject
      .pipe(debounceTime(200))
      .subscribe((c) => {
        this.processTemplate(c);
      });
    this.loadingSubject
      .pipe(
        debounceTime(200)
      )
      .subscribe((args) => {
        this.loading = args === true;
      });
  },
  methods: {
    goBack() {
      this.$emit('go-back');
    },
    routeTo(pageURL, pageKey = '') {
      this.$emit('route-to', { pageURL, pageKey });
    },
    onCultureChange() {
      // this.changeSubject.next(true);
    },
    onCulturesChange(newValue) {
      this.culturesObject = this.parseJsonData(this.unescapeHtml(newValue));
      this.changeSubject.next(true);
    },
    onUriChange(newValue) {
      console.log('onUriChange', newValue);
      if (!newValue || newValue == '') {
        return;
      }

      this.templateUrl = newValue;

      if (this.preview) {
        this.payloadUpdated = true;
      }

      this.uriUpdated = true;

      this.changeSubject.next(true);
    },
    onPayloadChange(newValue) {
      if (!newValue || newValue == '') {
        return;
      }

      this.payloadObject = this.parseJsonData(this.unescapeHtml(newValue));

      if (this.preview) {
        this.uriUpdated = true;
      }

      this.payloadUpdated = true;

      this.changeSubject.next(false);
    },
    async setCache(key, value) {
      const item = {
        value,
        expiry: new Date().getTime() + this.expirationTimeout,
      };
      await localForage.setItem(key, item);
    },
    async getCache(key) {
      if (this.preview) {
        // Do not cache in preview mode
        return null;
      }
      const item = await localForage.getItem(key);
      if (item && item.expiry > new Date().getTime()) {
        return item.value;
      }
      await localForage.removeItem(key);
      return null;
    },
    replaceFieldContent(field, element) {
      const content = field.content && field.content != '' ? field.content : field.description;

      console.log('replace template entry');

      const fieldType = field.fieldType.toLowerCase();
      const fieldHandlers = {
        link: () => {
          if (content && content != '' && content != 'null') {
            element.href = content;
            console.log('element.href', element.href);

            if (element.tagName != 'A') {
              element.innerHTML = content;
            }
          }
        },
        richtext: () => {
          console.log('element.innerHTML', content);

          this.replaceRichText(element, content, 0);
        },
        static: () => {
          // do nothing
          element.setAttribute('dir', 'auto');
        },
        imageurl: () => {
          if (!content || content == '' || content == 'null') {
            console.log('imageurl field has no content', field);
            return;
          }

          if (element.tagName.toUpperCase() == 'IMG') {
            element.src = content;
          } else {
            let img = document.createElement('img');
            img.src = content;
            img.style.width = '100%';
            img.style.margin = '0 auto';
            element.appendChild(img);
          }
        },
        videourl: () => {

          if (!content || content == '' || content == 'null') {
            console.log('videourl field has no content', field);
            return;
          }

          if (element.tagName.toUpperCase() == 'VIDEO') {
            let source = element.querySelector('source');
            if (source) {
              source.src = content;
            } else {
              element.src = content;
            }
          } 
          else {
            let videoPlayer = document.createElement('video');
            videoPlayer.className = 'video-js';
            videoPlayer.id = field.key;
            videoPlayer.ref = 'videoPlayer';
            videoPlayer.style.width = '100%';
            videoPlayer.style.height = '25vh';
            videoPlayer.src = content;
         
            element.appendChild(videoPlayer); 
          }
        },
        sms: () => {
          if (element.tagName.toUpperCase() == 'A') {
            element.href = 'sms:' + content;

            if (element.innerHTML.trim().length == 0)
              element.innerHTML = content;
          } else {
            let a = document.createElement('a');
            a.href = 'sms:' + content;
            a.innerHTML = content;

            element.appendChild(a);
          }
        },
        telephone: () => {
          if (element.tagName.toUpperCase() == 'A') {
            element.href = 'tel:' + content;

            if (element.innerHTML.trim().length == 0)
              element.innerHTML = content;
          } else {
            let a = document.createElement('a');
            a.href = 'tel:' + content;
            a.innerHTML = content;

            element.appendChild(a);
          }
        },
        email: () => {
          if (element.tagName.toUpperCase() == 'A') {
            element.href = 'mailto:' + content;

            if (element.innerHTML.trim().length == 0)
              element.innerHTML = content;
          } else {
            let a = document.createElement('a');
            a.href = 'mailto:' + content;
            a.innerHTML = content;

            element.appendChild(a);
          }
        },
        default: () => {
          element.innerHTML = field.content;
        }
      };

      (fieldHandlers[fieldType] || fieldHandlers.default)();
    },
    replaceRichText(element, content, iteration) {
      element.setAttribute('dir', 'auto');
      if (this.isValidInnerHtml(content, element.tagName)) {
        element.innerHTML = content;
      } else {
        // If element is a block element or content has block elements - replace element with div
        let divElement = document.createElement('div');
        divElement.innerHTML = content;

        // Copy all attributes of the original element to the new div
        Array.prototype.slice.call(element.attributes).forEach(function (attribute) {
          divElement.setAttribute(attribute.name, attribute.value);
        });

        divElement.classList.add(`u-compat-${element.tagName.toLowerCase()}`);

        element.replaceWith(divElement);
        iteration++;
        if (iteration < maxCompatCheckLevels) {

          // We will check the content up to certain number of levels upwards to follow specification, but we 
          // want to avoid traversing entire document
          const parentElement = divElement.parentElement;
          if (!this.isValidInnerHtml(parentElement.innerHTML, parentElement.tagName)) {
            this.replaceRichText(parentElement, parentElement.innerHTML, iteration);
          }
        }
      }
    },
    setLanguageSelector(root) {
      var selector = root.querySelector('#culture-selector');
      var dropdownDiv = root.querySelector('#culture-div');

      if (selector && dropdownDiv) {
        selector.setAttribute('autocomplete', 'off');

        if (this.culturesObject) {
          const cultures = this.culturesObject;
          selector.innerHTML = '';

          for (var entry in cultures) {
            if (cultures[entry].Published && cultures[entry].Culture) {
              const opt = document.createElement('option');
              opt.value = cultures[entry].Culture.cultureCode;
              opt.innerHTML = cultures[entry].Culture.displayName;

              selector.appendChild(opt);

              if (opt.value === this.culture) {
                opt.setAttribute('selected', 'selected');
              }
            }
          }


          if (this.preview) {
            selector.setAttribute('disabled', true);
          }

          if (selector.options.length < 2) {
            dropdownDiv.style.display = 'none';
          }

        } else {
          dropdownDiv.style.display = 'none';
        }

      }
    },
    toggleFieldVisibility(field, root) {
      if (field.hidden === true) {
        root.querySelector(`[fieldKey="${field.key}"]`).style.display = 'none';
      }
    },
    replaceFields(root) {
      const fields = this.payloadObject.fields;
      if (fields) {
        for (let field of fields) {
          let elements = root.querySelectorAll(`[fieldKey="${field.key}"]`);

          if (elements) {
            for (let element of elements) {
              this.replaceFieldContent(field, element);
              this.toggleFieldVisibility(field, root);
            }
          }
        }
      }

      this.hideNonExistingFields(root)

      const pages = this.payloadObject.pages;
      if (pages) {
        for (let page of pages) {
          const element = this.findPageNavElementByKey(page.key);
          if (element && page.hidden) {
            element.style.display = 'none';
          }
        }
      }
    },
    hideNonExistingFields(root) {
      const fields = this.payloadObject.fields;
      if (fields) {
        let nodeList = root.querySelectorAll('[fieldKey]');

        if (nodeList && nodeList.length > 0) {
          for (let i = 0; i < nodeList.length; i++) {
            const fieldKey = nodeList[i].getAttribute('fieldKey');
            const filtered = fields.filter(item => item.key === fieldKey);

            if (filtered.length == 0) {
              nodeList[i].style.display = 'none';
            }
          }
        }
      }
    },
    initializeVideoElements(root) {
      let elements = root.querySelectorAll('video');
      
      if (elements) {
        for (let element of elements) {
          if (element.tagName.toUpperCase() == 'VIDEO') {
            try {
              let player = videojs(element, this.videoJsOptions, () => {
                  player.log('onPlayerReady', this);
              });
            } catch (error) {
              console.error('Error initializing video elements:', error);
            }
          }
        }
      }    
    },
    disposeVideoPlayers() {
      // dispose of players
      let players = document.getElementsByClassName('vjs-tech');

      for (var i = 0; i < players.length; i++) {
        let thisPlayer = videojs(players[i].id);
        thisPlayer.dispose();
        console.log('did it dispose?', thisPlayer.isDisposed())
      }
    },
    findPageNavElementByKey(key) {
      var items = document.getElementsByClassName('item');

      for (var i = 0; i < items.length; i++) {
        if (items[i].getAttribute('onclick').includes(key)) {
          return items[i];
        }
      }
      return null;
    },
    toggleIfHiddenVisibility(root) {
      const fields = this.payloadObject.fields;

      if (fields) {
        let nodeList = root.querySelectorAll('[hideifhidden]');

        if (nodeList && nodeList.length > 0) {
          for (let i = 0; i < nodeList.length; i++) {
            const sectionFieldList = nodeList[i].getAttribute('hideifhidden').split(' ').join('').split(',');
            const filtered = fields.filter(item => sectionFieldList.includes(item.key));

            if (filtered.length == 0) {
              nodeList[i].style.display = 'none';
              continue;
            }

            const hide = filtered.every(item => item.hidden === true);

            if (hide) {
              nodeList[i].style.display = 'none';
            }
          }
        }
      }

    },
    processHiddenPages(root) {
      const pages = this.payloadObject.pages;
      if (pages) {
        for (let i in pages) {
          if (pages[i].hidden) {
            let element = root.querySelector(`[onclick*=${pages[i].key}]`);

            if (element) { element.remove(); }
          }
        }
      }
    },
    processGoBackLinks(root) {
      // This is done to prevent content team from using goBack and href simulateneously
      const links = root.querySelectorAll('a[onclick*="goBack()"]');
        links.forEach(link => {
          if (link.getAttribute('onclick').includes('goBack')) {
            link.removeAttribute('href');
          }
        });
    },
    async processTemplate(isUriChange) {
      if (!this.templateUrl) {
        this.loadingSubject.next(true);
        return;
      }

      if (!this.payloadObject.fields) {
        this.loadingSubject.next(true);
        return;
      }

      if (!this.uriUpdated || !this.payloadUpdated)
        return;

      this.$emit('loading', true);

      if (this.cultures) {
        this.culturesObject = this.parseJsonData(this.unescapeHtml(this.cultures));
      }

      this.error = false;
      let switchFrames = true;
      this.index++;
      this.frameToggle = !this.frameToggle;

      const activeFrame = this.frameToggle ? this.$refs.templateDiv1 : this.$refs.templateDiv2;
      const inactiveFrame = this.frameToggle ? this.$refs.templateDiv2 : this.$refs.templateDiv1;

      // Saving scroll position for later use
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      try {
        let templateResponseData = await this.getCache(this.templateUrl);
        if (!templateResponseData) {
          const templateResponse = await axios.get(this.templateUrl,
            {
              headers: {
                'Cache-Control': 'no-cache'
              }
            });
          await this.setCache(this.templateUrl, templateResponse.data);
          templateResponseData = templateResponse.data;
        }

        let htmlString = this.prepareHtmlString(templateResponseData);

        try {
          // Next line will fail if compilation is not successful. We are reporting error to the logs
          // but allowing template to proceed, but without Vue functionality
          compile(htmlString);
          htmlString = this.createDynamicTemplate(htmlString);
        } catch (error) {
          console.error('Failed to compile template, using template without compilation: ' + error);
        }

        this.disposeVideoPlayers();  // dispose of any video players when switching frames

        // Parse processed document and run replace logic used in previous version
        let parser = new DOMParser();
        let parsedHtml = parser.parseFromString(htmlString, 'text/html');

        this.replaceFields(parsedHtml.documentElement);
        this.toggleIfHiddenVisibility(parsedHtml.documentElement);
        this.processHiddenPages(parsedHtml.documentElement);
        this.setLanguageSelector(parsedHtml.documentElement);
        this.processGoBackLinks(parsedHtml.documentElement);

        if (activeFrame && activeFrame.innerHTML != parsedHtml.documentElement.innerHTML) {
          activeFrame.innerHTML = parsedHtml.documentElement.innerHTML;
          this.initializeVideoElements(activeFrame);
        }
      } catch (error) {
        this.error = true;
        console.error('Failed to process template:', error);
      }
      finally {

        this.uriUpdated = false;
        this.payloadUpdated = false;
        
        if (!isUriChange) {
          // Restore scroll position
          window.scrollTo(scrollX, scrollY);
        } else {
          // Scroll to top
          window.scrollTo(0, 0);
        }

        activeFrame.style.zIndex = this.index;

        if (switchFrames) {
          activeFrame.style.display = 'block';
          //activeFrame.classList.remove("fade-out-slide");
          //activeFrame.classList.add("fade-in-slide");

          //inactiveFrame.classList.remove("fade-in-slide");
          //inactiveFrame.classList.add("fade-out-slide");
        }

        if (this.preview) {
          activeFrame.style.opacity = 1;
          inactiveFrame.style.opacity = 0;
        }

        inactiveFrame.style.display = 'none';

        this.loadingSubject.next(false);
        this.$emit('loading', false);
      }
    },
    prepareHtmlString(htmlString) {
      // Replace all width="x%" with style="width: x%;" (for height too), since Vue doesn't think those are correct values
      let parser = new DOMParser();
      let parsedHtml = parser.parseFromString(htmlString, 'text/html');

      parsedHtml.documentElement.querySelectorAll('[width]').forEach(element => {
        element.style.width = element.getAttribute('width');
        element.removeAttribute('width');
      });

      parsedHtml.documentElement.querySelectorAll('[height]').forEach(element => {
        element.style.height = element.getAttribute('height');
        element.removeAttribute('height');
      });

      return parsedHtml.documentElement.innerHTML;
    },
    createDynamicTemplate(modifiedHtmlString) {
      const template = this.payloadObject;

      const dynamicTemplate = {
        name: 'dynamicTemplate',
        props: {
          pages: {
            type: Array,
            default: []
          },
          fields: {
            type: Array,
            default: []
          },
          categories: {
            type: Array,
            default: []
          },
          template: {
            type: Object,
            default: {}
          },
          menu: {
            type: Object,
            default: {}
          },
          content: {
            type: Array,
            default: []
          }
        },
        template: modifiedHtmlString
      }

      const dynamicApp = createApp({
        data() {
          return {
            rawHtml: modifiedHtmlString
          }
        },
        render() {
          const templateData = {
            pages: template.pages ?? [],
            fields: template.fields ?? [],
            categories: template.categories ?? [],
            template: template.template ?? {},
            menu: template.menu ?? {},
            content: []
          };

          let validVarNamePattern = /^[a-zA-Z_$][0-9a-zA-Z_$]*$/;

          for (let field of templateData.fields) {
            if (!field.hidden && validVarNamePattern.test(field.key)) {
              templateData.content[field.key] = field.content ?? field.description ?? '';
            }
          }

          return h(dynamicTemplate, templateData);
        }
      })

      const el = document.createElement('div');
      const mountedDynamicApp = dynamicApp.mount(el);

      let html = mountedDynamicApp.$el.outerHTML;
      if (!html) {
        html = this.getRootElement(mountedDynamicApp.$el).outerHTML;
      }
      return html;
    },
    unescapeHtml(html) {
      // Replace &quot; with "
      let uriComponent = html.replace(/&quot;/g, '"');

      // Replace &lt; with <
      uriComponent = uriComponent.replace(/&lt;/g, '<');

      // Replace &gt; with >
      uriComponent = uriComponent.replace(/&gt;/g, '>');

      // Replace &amp; with &
      uriComponent = uriComponent.replace(/&amp;/g, '&');

      // Replace &apos; and &#39; with '
      uriComponent = uriComponent.replace(/&apos;/g, '\'');
      uriComponent = uriComponent.replace(/&#39;/g, '\'');

      // Use he.decode to handle other HTML entities
      uriComponent = he.decode(uriComponent);

      return uriComponent;
    },
    parseJsonData(source) {
      try {
        return JSON.parse(this.unescapeHtml(source));
      } catch (error) {
        console.error('Failed to parse JSON data: ' + source);
        return {};
      }
    },

    getRootElement(element) {
      let currentElement = element;

      while (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
      }

      return currentElement;
    },
    setObservers() {
      const currentView = document.getElementsByTagName('u-template-view')[0];
      if (currentView) {
        const observer = new MutationObserver(this.observersMutationCallback);
        observer.observe(currentView, {
          attributes: true,
          attributeOldValue: true
        });
      }

    },
    observersMutationCallback(mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          if (mutation.attributeName == 'uri') {
            this.onUriChange(mutation.target.getAttribute(mutation.attributeName));
          } else if (mutation.attributeName == 'payload') {
            this.onPayloadChange(mutation.target.getAttribute(mutation.attributeName));
          }
        }
      }
    },
    isValidInnerHtml(htmlString, elementTag) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const allElements = doc.querySelectorAll('*');

      const canContainBlockElements =
        blockElementExclusions.includes(elementTag.toUpperCase());

      for (let element of allElements) {
        if (blockElements.includes(element.tagName) && canContainBlockElements) {
          return false;
        }
      }

      return true;
    }
  },
  mounted() {
    this.setObservers();
    this.onPayloadChange(this.payload);
    this.onUriChange(this.uri);
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Montserrat:400,700');

.fade-in-slide {
  animation: fadeIn 0.2s;
  animation-fill-mode: forwards;
}

.fade-out-slide {
  animation: fadeOut 0s;
  animation-fill-mode: forwards;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.spinner {
  width: 40px;
  height: 40px;
  top: calc(50% - 20px);
  left: calc(50% - 20px);
  position: absolute;
}

.double-bounce1,
.double-bounce2 {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #969494;
  opacity: 0.6;
  position: absolute;
  top: 0;
  left: 0;

  -webkit-animation: sk-bounce 2.0s infinite ease-in-out;
  animation: sk-bounce 2.0s infinite ease-in-out;
}

.double-bounce2 {
  -webkit-animation-delay: -1.0s;
  animation-delay: -1.0s;
}

@-webkit-keyframes sk-bounce {

  0%,
  100% {
    -webkit-transform: scale(0.0)
  }

  50% {
    -webkit-transform: scale(1.0)
  }
}

@keyframes sk-bounce {

  0%,
  100% {
    transform: scale(0.0);
    -webkit-transform: scale(0.0);
  }

  50% {
    transform: scale(1.0);
    -webkit-transform: scale(1.0);
  }
}

.error {
  text-align: center;
  padding: 16px;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  -webkit-transform: translateY(-50%);
  font-family: 'Montserrat', sans-serif;
  color: grey;
}

.error h1 {
  font-size: calc(17vw + 40px);
  opacity: .8;
  margin: 0px;
}

.error p {
  opacity: .4;
  font-size: 14px;
  margin: 8px 0 38px 0;
  font-weight: bold
}
</style>
